/**
 * 95. Industry Pack Manager — pack install; templates; custom entity import.
 * Industry packs extend the platform (Manufacturing, Retail, Construction,
 * Healthcare, Education, Logistics, Hospitality, Professional Services, …).
 *
 * Arabic and English (C7): every word from the catalogs; pack names are the
 * pack definitions' own.
 */
import { useId } from "react";
import { Card, PageHeader, Badge, DataTable, Skeleton } from "@/components/ui";
import { PendingButton, PendingNote } from "@/components/PendingAction";
import { useApiList } from "@/lib/api/hooks";
import { useTranslation } from "@/lib/i18n";
import { listTitle } from "@/lib/i18n/format";
import { formatQuantity } from "@/lib/money";

interface IndustryPack {
  id: string;
  code: string;
  name: string;
  version: number;
  installedVersion: number | null;
  entityCount: number;
  workflowCount: number;
}

export function IndustryPackManagerPage() {
  const { t } = useTranslation();
  const installId = useId();
  const manageId = useId();
  const { data, isLoading } = useApiList<IndustryPack>(
    ["localization", "industry-packs"],
    "/localization/industry-packs",
  );

  return (
    <div>
      <PageHeader
        title={t("admin.industryPacks.title")}
        subtitle={t("admin.industryPacks.subtitle")}
        actions={
          <PendingButton variant="primary" reason="marketplace" slice="M4">
            {t("admin.industryPacks.browse")}
          </PendingButton>
        }
      />
      <PendingNote id={installId} reason="packInstall" slice="CF-2" />
      {data?.items.some((r) => r.installedVersion !== null) && (
        <PendingNote id={manageId} reason="packUpgrade" slice="CF-4" />
      )}
      {isLoading || !data ? (
        <Skeleton rows={8} />
      ) : (
        <Card title={listTitle(t("admin.industryPacks.list"), data.total)}>
          <DataTable<IndustryPack>
            rows={data.items}
            columns={[
              { key: "name", label: t("admin.industryPacks.pack") },
              {
                key: "code",
                label: t("fields.code"),
                render: (r) => <span className="atlas-mono">{r.code}</span>,
              },
              {
                key: "entityCount",
                label: t("admin.industryPacks.entities"),
                numeric: true,
                render: (r) => formatQuantity(r.entityCount, 0),
              },
              {
                key: "workflowCount",
                label: t("admin.industryPacks.workflows"),
                numeric: true,
                render: (r) => formatQuantity(r.workflowCount, 0),
              },
              {
                key: "installedVersion",
                label: t("fields.status"),
                render: (r) =>
                  r.installedVersion === null ? (
                    <Badge tone="default">
                      {t("admin.industryPacks.notInstalled")}
                    </Badge>
                  ) : (
                    <Badge tone="success">
                      {t("shared.versionNumber", {
                        version: r.installedVersion,
                      })}
                    </Badge>
                  ),
              },
              {
                key: "id",
                label: t("fields.actions"),
                render: (r) =>
                  r.installedVersion === null ? (
                    <PendingButton
                      size="sm"
                      variant="primary"
                      reasonId={installId}
                    >
                      {t("admin.industryPacks.install")}
                    </PendingButton>
                  ) : (
                    <PendingButton size="sm" reasonId={manageId}>
                      {t("admin.industryPacks.manage")}
                    </PendingButton>
                  ),
              },
            ]}
          />
        </Card>
      )}
    </div>
  );
}
